import {
  Dialog,
  TextField,
  Button,
  Select,
  MenuItem,
  IconButton,
  Box,
  Avatar,
  Grid,
  Typography,
  Chip,
  useTheme,
  Slide,
  InputAdornment,
} from "@mui/material";
import { X, Eye, EyeOff, User } from "lucide-react";
import { useState, useEffect, useMemo, forwardRef } from "react";
import { getAllRolesApi } from "../../api/users.api";
import { getAllTeamsApi } from "../../api/teams.api";

const TEAM_OPTIONS = ["Admin", "Designer", "Sales Executive", "Support"];

const teamColors = {
  Admin: "#7C3AED",
  Designer: "#EC4899",
  "Sales Executive": "#F59E0B",
  Support: "#10B981",
};

const SlideTransition = forwardRef(function SlideTransition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export default function UserFormModal({
  open,
  onClose,
  onSave,
  user,
  rolesList = [],
}) {
  const theme = useTheme();

  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    status: "active",
    roles: [],
    team: "",
  });
  const [touched, setTouched] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [apiErrors, setApiErrors] = useState({});
  const [teamsList, setTeamsList] = useState([]);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    const fetchTeams = async () => {
      try {
        const response = await getAllTeamsApi();
        setTeamsList(response.data.data || []);
      } catch (err) {
        console.error("Failed to load teams:", err);
      }
    };
    if (open) {
      fetchTeams();
    }
  }, [open]);

  useEffect(() => {
    if (user) {
      const currentRole = user.roles?.length
        ? typeof user.roles[0] === "string"
          ? user.roles[0]
          : user.roles[0].name
        : "";

      setForm({
        name: user.name || "",
        email: user.email || "",
        password: "",
        status: user.status || "active",
        roles: currentRole ? [currentRole] : [],
        team: user.team || "",
      });
      setTouched({});
    } else {
      setForm({
        name: "",
        email: "",
        password: "",
        status: "active",
        roles: [],
        team: "",
      });
      setTouched({});
    }
    setShowPassword(false);
  }, [user, open]);

  const roleColors = useMemo(
    () => ({
      superadmin: theme.roleColors.superadmin,
      admin: theme.roleColors.admin,
      manager: theme.roleColors.manager,
      user: theme.roleColors.user,
      employee: theme.roleColors.employee,
    }),
    []
  );

  const normalize = (s) =>
    (s || "")
      .toString()
      .trim()
      .toLowerCase()
      .replace(/[\s\-_]/g, "");

  const getRoleHex = (roleName) => {
    if (!roleName) return "#9CA3AF";
    const key = normalize(roleName);
    return roleColors[key] ?? "#9CA3AF";
  };

  const hexToRgba = (hex, alpha = 1) => {
    const h = hex.replace("#", "");
    const full =
      h.length === 3
        ? h
            .split("")
            .map((c) => c + c)
            .join("")
        : h;
    const bigint = parseInt(full, 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const validate = () => {
    const errors = {};
    if (!form.name.trim()) errors.name = "Name is required";
    if (!form.email.trim()) {
      errors.email = "Email is required";
    } else {
      const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRe.test(form.email)) errors.email = "Enter a valid email";
    }

    if (!user && !form.password) errors.password = "Password is required";
    return errors;
  };

  const errors = validate();
  const isValid = Object.keys(errors).length === 0;

  const handleSave = async () => {
    setTouched({ name: true, email: true, password: true });
    setApiErrors({});

    if (!isValid) return;

    try {
      setSubmitting(true);
      await onSave(form);
    } catch (err) {
      const message =
        err?.response?.data?.message || err?.response?.data?.error || "";

      if (
        err?.response?.status === 409 ||
        message.toLowerCase().includes("email")
      ) {
        setApiErrors({ email: "Email is already registered" });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const initials = useMemo(() => {
    if (!form.name) return "U";
    return form.name
      .split(" ")
      .map((p) => p[0])
      .slice(0, 2)
      .join("")
      .toUpperCase();
  }, [form.name]);

  const labelSx = user
    ? {
        fontWeight: 700,
        fontSize: 11.5,
        mb: 0.75,
        color: "#374151",
        textTransform: "uppercase",
        letterSpacing: "0.06em",
      }
    : {
        fontWeight: 600,
        fontSize: 13,
        mb: 0.75,
        color: "#374151",
      };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      TransitionComponent={SlideTransition}
      TransitionProps={{ timeout: 350 }}
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: "hidden",
          boxShadow: "0 25px 60px -12px rgba(0, 0, 0, 0.2)",
          maxWidth: 750,
        },
      }}
    >
      <Box sx={{ display: "flex", minHeight: { xs: "auto", sm: 480 } }}>
        {/* ─── Left Gradient Panel ─── */}
        <Box
          sx={{
            display: { xs: "none", sm: "flex" },
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            width: 230,
            minWidth: 230,
            background: `linear-gradient(180deg, ${theme.sidebar.bgStart} 0%, ${theme.sidebar.bgEnd} 100%)`,
            p: 4,
            position: "relative",
            "@keyframes fadeInScale": {
              from: { opacity: 0, transform: "scale(0.92)" },
              to: { opacity: 1, transform: "scale(1)" },
            },
            animation: "fadeInScale 0.4s ease forwards",
          }}
        >
          {/* Profile Preview label (add mode) */}
          {!user && (
            <Typography
              sx={{
                position: "absolute",
                top: 28,
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.15em",
                textTransform: "uppercase",
                color: "rgba(255,255,255,0.45)",
              }}
            >
              Profile Preview
            </Typography>
          )}

          {/* Avatar */}
          <Avatar
            sx={{
              width: 80,
              height: 80,
              bgcolor: "#fff",
              color: user ? "#1F2937" : theme.palette.primary.main,
              fontSize: 32,
              fontWeight: 700,
              boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
              mb: 1.5,
              transition: "transform 0.3s ease",
              "&:hover": { transform: "scale(1.05)" },
            }}
          >
            {user ? (
              initials
            ) : form.name ? (
              initials
            ) : (
              <User size={36} strokeWidth={1.5} />
            )}
          </Avatar>

          {/* Name */}
          <Typography
            sx={{
              color: "#fff",
              fontWeight: 700,
              fontSize: 16,
              textAlign: "center",
              mb: 0.25,
            }}
          >
            {form.name || (user ? "User" : "New User")}
          </Typography>

          {/* Email */}
          <Typography
            sx={{
              color: "rgba(255,255,255,0.55)",
              fontSize: 12,
              textAlign: "center",
              mb: 1.5,
            }}
          >
            {form.email?.trim() || (user ? "—" : "user@example.com")}
          </Typography>

          {/* Status Chip */}
          <Chip
            size="small"
            icon={
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  bgcolor:
                    form.status === "active" ? "#10B981" : "#EF4444",
                  ml: 0.5,
                }}
              />
            }
            label={
              form.status === "active"
                ? user
                  ? "Active Status"
                  : "Status: Active"
                : user
                ? "Inactive"
                : "Status: Inactive"
            }
            sx={{
              bgcolor: "rgba(255,255,255,0.08)",
              color: "#fff",
              border: `1px solid ${
                form.status === "active"
                  ? "rgba(16,185,129,0.4)"
                  : "rgba(239,68,68,0.4)"
              }`,
              fontWeight: 600,
              fontSize: 11,
              height: 26,
              borderRadius: 50,
              px: 0.5,
              transition: "all 0.3s ease",
            }}
          />
        </Box>

        {/* ─── Right Form Panel ─── */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            bgcolor: "#fff",
            minWidth: 0,
            width: 0,
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              px: { xs: 2.5, sm: 3.5 },
              pt: 3,
              pb: 1,
            }}
          >
            <Box>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: 18, sm: 20 },
                  color: user
                    ? theme.palette.primary.main
                    : "#111827",
                  mb: 0.25,
                }}
              >
                {user ? "Edit User" : "Add New User"}
              </Typography>
              <Typography
                sx={{ fontSize: 13, color: "#9CA3AF" }}
              >
                {user
                  ? "Update details and assign user roles"
                  : "Create an account for your team member"}
              </Typography>
            </Box>

            <IconButton
              onClick={onClose}
              size="small"
              aria-label="close"
              sx={{
                mt: 0.5,
                color: "#9CA3AF",
                transition: "all 0.2s ease",
                "&:hover": {
                  color: "#374151",
                  transform: "rotate(90deg)",
                },
              }}
            >
              <X size={18} />
            </IconButton>
          </Box>

          {/* Form Content */}
          <Box
            sx={{
              flex: 1,
              px: { xs: 2.5, sm: 3.5 },
              py: 2,
              overflowY: "auto",
            }}
          >
            <Grid container spacing={2}>
              {/* Row 1: Name + Email */}
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography sx={labelSx}>
                  {user ? "Name" : "Full Name"}
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder={!user ? "e.g. John Doe" : ""}
                  value={form.name}
                  onChange={(e) =>
                    setForm({ ...form, name: e.target.value })
                  }
                  onBlur={() =>
                    setTouched((t) => ({ ...t, name: true }))
                  }
                  error={Boolean(touched.name && errors.name)}
                  helperText={
                    touched.name && errors.name ? errors.name : ""
                  }
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      bgcolor: "#F9FAFB",
                      "&:hover fieldset": {
                        borderColor: theme.palette.primary.main,
                      },
                    },
                  }}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography sx={labelSx}>
                  Email Address
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder={!user ? "john@company.com" : ""}
                  value={form.email}
                  onChange={(e) => {
                    setForm({ ...form, email: e.target.value });
                    setApiErrors((prev) => ({ ...prev, email: null }));
                  }}
                  onBlur={() =>
                    setTouched((t) => ({ ...t, email: true }))
                  }
                  error={Boolean(
                    (touched.email && errors.email) || apiErrors.email
                  )}
                  helperText={
                    apiErrors.email
                      ? apiErrors.email
                      : touched.email && errors.email
                      ? errors.email
                      : ""
                  }
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      bgcolor: "#F9FAFB",
                      "&:hover fieldset": {
                        borderColor: theme.palette.primary.main,
                      },
                    },
                  }}
                />
              </Grid>

              {/* Row 2: Password (add mode only) */}
              {!user && (
                <Grid size={{ xs: 12 }}>
                  <Typography sx={labelSx}>Password</Typography>
                  <TextField
                    fullWidth
                    size="small"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter a secure password"
                    value={form.password}
                    onChange={(e) =>
                      setForm({ ...form, password: e.target.value })
                    }
                    onBlur={() =>
                      setTouched((t) => ({ ...t, password: true }))
                    }
                    error={Boolean(touched.password && errors.password)}
                    helperText={
                      touched.password && errors.password
                        ? errors.password
                        : ""
                    }
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() =>
                              setShowPassword(!showPassword)
                            }
                            edge="end"
                            size="small"
                            sx={{ color: "#9CA3AF" }}
                          >
                            {showPassword ? (
                              <EyeOff size={16} />
                            ) : (
                              <Eye size={16} />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        bgcolor: "#F9FAFB",
                        "&:hover fieldset": {
                          borderColor: theme.palette.primary.main,
                        },
                      },
                    }}
                  />
                </Grid>
              )}

              {/* Row 3: Status + Role + Team (layout differs for add vs edit) */}
              {user ? (
                <>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography sx={labelSx}>Status</Typography>
                    <Select
                      fullWidth
                      size="small"
                      value={form.status}
                      onChange={(e) =>
                        setForm({ ...form, status: e.target.value })
                      }
                      sx={{
                        borderRadius: 2,
                        bgcolor: "#F9FAFB",
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: theme.palette.primary.main,
                        },
                      }}
                    >
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="inactive">Inactive</MenuItem>
                    </Select>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography sx={labelSx}>User Role</Typography>
                    <Select
                      fullWidth
                      size="small"
                      value={form.roles[0] || ""}
                      onChange={(e) =>
                        setForm({ ...form, roles: [e.target.value] })
                      }
                      displayEmpty
                      sx={{
                        borderRadius: 2,
                        bgcolor: "#F9FAFB",
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: theme.palette.primary.main,
                        },
                      }}
                    >
                      <MenuItem value="" disabled>
                        <em>Select role</em>
                      </MenuItem>
                      {rolesList.map((role) => (
                        <MenuItem key={role._id} value={role.name}>
                          {role.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </Grid>

                  <Grid size={{ xs: 12 }}>
                    <Typography sx={labelSx}>
                      Team Assignment
                    </Typography>
                    <Select
                      fullWidth
                      size="small"
                      value={form.team || ""}
                      onChange={(e) =>
                        setForm({ ...form, team: e.target.value })
                      }
                      displayEmpty
                      sx={{
                        borderRadius: 2,
                        bgcolor: "#F9FAFB",
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: theme.palette.primary.main,
                        },
                      }}
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {teamsList.map((team) => (
                        <MenuItem key={team._id} value={team.name}>
                          {team.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </Grid>
                </>
              ) : (
                <>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Typography sx={labelSx}>Status</Typography>
                    <Select
                      fullWidth
                      size="small"
                      value={form.status}
                      onChange={(e) =>
                        setForm({ ...form, status: e.target.value })
                      }
                      sx={{
                        borderRadius: 2,
                        bgcolor: "#F9FAFB",
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: theme.palette.primary.main,
                        },
                      }}
                    >
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="inactive">Inactive</MenuItem>
                    </Select>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Typography sx={labelSx}>Select Role</Typography>
                    <Select
                      fullWidth
                      size="small"
                      value={form.roles[0] || ""}
                      onChange={(e) =>
                        setForm({ ...form, roles: [e.target.value] })
                      }
                      displayEmpty
                      sx={{
                        borderRadius: 2,
                        bgcolor: "#F9FAFB",
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: theme.palette.primary.main,
                        },
                      }}
                    >
                      <MenuItem value="" disabled>
                        <em>Select</em>
                      </MenuItem>
                      {rolesList.map((role) => (
                        <MenuItem key={role._id} value={role.name}>
                          {role.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Typography sx={labelSx}>Assign Team</Typography>
                    <Select
                      fullWidth
                      size="small"
                      value={form.team || ""}
                      onChange={(e) =>
                        setForm({ ...form, team: e.target.value })
                      }
                      displayEmpty
                      sx={{
                        borderRadius: 2,
                        bgcolor: "#F9FAFB",
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: theme.palette.primary.main,
                        },
                      }}
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {teamsList.map((team) => (
                        <MenuItem key={team._id} value={team.name}>
                          {team.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </Grid>
                </>
              )}

              {/* Selected Role Chip */}
              <Grid size={{ xs: 12 }}>
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    alignItems: "center",
                    flexWrap: "wrap",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: "#6B7280",
                    }}
                  >
                    Selected:
                  </Typography>

                  {form.roles[0] ? (
                    <Chip
                      label={form.roles[0]}
                      size="small"
                      onDelete={() =>
                        setForm({ ...form, roles: [] })
                      }
                      sx={{
                        fontWeight: 600,
                        borderRadius: 1.5,
                        bgcolor: hexToRgba(
                          getRoleHex(form.roles[0]),
                          0.1
                        ),
                        color: getRoleHex(form.roles[0]),
                        border: `1px solid ${hexToRgba(
                          getRoleHex(form.roles[0]),
                          0.25
                        )}`,
                        transition: "all 0.2s ease",
                        "& .MuiChip-deleteIcon": {
                          color: getRoleHex(form.roles[0]),
                          fontSize: 16,
                          "&:hover": {
                            color: getRoleHex(form.roles[0]),
                            opacity: 0.7,
                          },
                        },
                      }}
                    />
                  ) : (
                    <Typography
                      sx={{ fontSize: 13, color: "#9CA3AF" }}
                    >
                      No role selected
                    </Typography>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Actions */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 1.5,
              px: { xs: 2.5, sm: 3.5 },
              py: 2.5,
              borderTop: "1px solid #F3F4F6",
            }}
          >
            <Button
              onClick={onClose}
              sx={{
                textTransform: "none",
                borderRadius: 2,
                px: 2.5,
                py: 0.75,
                fontSize: 13,
                fontWeight: 500,
                color: "#6B7280",
                transition: "all 0.2s ease",
                "&:hover": {
                  bgcolor: "#F3F4F6",
                },
              }}
            >
              Cancel
            </Button>

            <Button
              variant="contained"
              onClick={handleSave}
              disabled={!isValid || submitting}
              sx={{
                textTransform: "none",
                borderRadius: 2,
                px: 3,
                py: 1,
                fontSize: 13,
                fontWeight: 700,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.avatarGradients_1.avatarEnd} 100%)`,
                color: "#fff",
                boxShadow: `0 4px 14px ${theme.palette.primary.main}30`,
                transition:
                  "all 0.25s cubic-bezier(0.23, 1, 0.32, 1)",
                "&:hover": {
                  transform: "translateY(-1px)",
                  boxShadow: `0 6px 20px ${theme.palette.primary.main}40`,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.avatarGradients_1.avatarEnd} 100%)`,
                },
                "&:active": {
                  transform: "translateY(0)",
                },
              }}
            >
              {user ? "Save Changes" : "Create Account"}
            </Button>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
}
